import { Component, OnInit, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { BloqueadosService } from './bloqueados.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AlertComponent } from '../../../alert/alert.component';
import { LoadingModalComponent } from '../../../loading-modal/loading-modal.component';
import { BloqueadoPipe } from './pipes/bloqueado.pipe';

@Component({
  selector: 'app-bloqueados',
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    AlertComponent,
    LoadingModalComponent,
    BloqueadoPipe
],
  templateUrl: './bloqueados.component.html',
  styleUrls: ['./bloqueados.component.scss'],
  providers: [BloqueadosService, BloqueadoPipe],
})
export class BloqueadosComponent implements OnInit {
  //Importacoes de alertas e loading
  @ViewChild(AlertComponent) alertComponent!: AlertComponent;
  @ViewChild(LoadingModalComponent) loadingComponent!: LoadingModalComponent;

  public currentBloqueados: Array<any> = [];
  public listBloqueados: Array<any> = [];
  public totalRegistros!: number;

  public filterNumber: string = "";
  public sortAsc: boolean = true;

  //Variaveis para o modal
  public modalRegistroBloqueado: boolean = false;
  public modalConfirmDelete: boolean = false;
  public numero: string = '';
  private numeroSelecionadoParaExcluir: string | null = null;

  public pageInfo: any = {
    currentPage: 1,
    itensPerPage: 5,
    totalPages: 0,
  };

  constructor(private bloqueadoService: BloqueadosService) {}

  ngOnInit() {
    this.getList();
  }

  public startRange(): number {
    if (!this.totalRegistros) return 0;
    return ((this.pageInfo.currentPage - 1) * this.pageInfo.itensPerPage) + 1;
  }

  public endRange(): number {
    const total = this.totalRegistros || 0;
    const end = this.pageInfo.currentPage * this.pageInfo.itensPerPage;
    return end > total ? total : end;
  }

  public alterQuantidadeView(event: Event) {
    const value = event.target as HTMLSelectElement;
    this.pageInfo.itensPerPage = Number(value.value);
    this.pageInfo.currentPage = 1;
    this.updateTotalPages();
    this.updateListBloqueados();
  }

  public getList(): void {
    this.bloqueadoService.getBloqueados().subscribe((response: any) => {
      this.listBloqueados = response || [];
      this.ordenarLista();
      this.pageInfo.currentPage = 1;
      this.recalcularPaginacao();
    });
  }

  public updateListBloqueados() {
    this.recalcularPaginacao();
  }

  public updateTotalPages() {
    this.pageInfo.totalPages = Math.max(
      1,
      Math.ceil(this.totalRegistros / this.pageInfo.itensPerPage)
    );
  }

  private applyFilter(lista: any[]): any[] {
    if (!this.filterNumber?.trim()) return lista;
    const filtro = this.normalizarNumero(this.filterNumber);
    return lista.filter((item) =>
      this.normalizarNumero(item.number).includes(filtro)
    );
  }

  private ordenarLista() {
    this.listBloqueados = [...this.listBloqueados].sort((a, b) => {
      const na = this.normalizarNumero(a.number);
      const nb = this.normalizarNumero(b.number);
      return this.sortAsc ? na.localeCompare(nb) : nb.localeCompare(na);
    });
  }

  public toggleSort() {
    this.sortAsc = !this.sortAsc;
    this.ordenarLista();
    this.recalcularPaginacao();
  }

  private recalcularPaginacao() {
    const filtrados = this.applyFilter(this.listBloqueados);
    this.totalRegistros = filtrados.length;
    this.updateTotalPages();
    if (this.pageInfo.currentPage > this.pageInfo.totalPages) {
      this.pageInfo.currentPage = this.pageInfo.totalPages;
    }
    const startIndex =
      (this.pageInfo.currentPage - 1) * this.pageInfo.itensPerPage;
    const endIndex = startIndex + this.pageInfo.itensPerPage;
    this.currentBloqueados = filtrados.slice(startIndex, endIndex);
  }

  public nextPage() {
    if (this.pageInfo.currentPage < this.pageInfo.totalPages) {
      this.pageInfo.currentPage += 1;
      this.recalcularPaginacao();
    }
  }

  public previousPage() {
    if (this.pageInfo.currentPage > 1) {
      this.pageInfo.currentPage -= 1;
      this.recalcularPaginacao();
    }
  }

  trackByIndex(index: number, item: any): number {
    return index; // Rastreia pelo índice
  }

  public desbloquearNumero(number: number | string) {
    this.numeroSelecionadoParaExcluir = String(number);
    this.modalConfirmDelete = true;
  }

  public confirmarDesbloqueio() {
    if (!this.numeroSelecionadoParaExcluir) return;
    const numero = this.normalizarNumero(this.numeroSelecionadoParaExcluir);
    if (!numero) {
      this.alertComponent.showAlert('Número inválido para desbloqueio.', 'error');
      this.modalConfirmDelete = false;
      return;
    }
    this.loadingComponent.activeLoading();
    this.bloqueadoService.desbloquearNumero(numero).subscribe(
      (response: any) => {
        this.listBloqueados = this.listBloqueados.filter(
          (item) => this.normalizarNumero(item.number) !== this.normalizarNumero(numero)
        );
        this.recalcularPaginacao();
        this.alertComponent.showAlert(
          'Número desbloqueado com sucesso',
          'success'
        );
        this.modalConfirmDelete = false;
        this.loadingComponent.inactiveLoading();
      },
      (error: any) => {
        this.alertComponent.showAlert('Erro ao desbloquear número', 'error');
        this.modalConfirmDelete = false;
        this.loadingComponent.inactiveLoading();
      }
    );
    this.numeroSelecionadoParaExcluir = null;
  }

  public cancelarDesbloqueio() {
    this.modalConfirmDelete = false;
    this.numeroSelecionadoParaExcluir = null;
  }

  //Funcoes do modal de registro
  public openModalRegistroBloqueado() {
    this.modalRegistroBloqueado = true;
  }

  public closeModalRegistroBloqueado() {
    this.numero = '';
    this.modalRegistroBloqueado = false;
  }

  public mostrarNumero() {
    this.numero = this.formatPhoneNumber(this.numero);
  }

  public formatPhoneNumber(phoneNumber: string): string {
    const cleaned = phoneNumber.replace(/\D/g, '').slice(0, 11);
    const match = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);

    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }

    return phoneNumber;
  }

  public async sendBloquear() {
    const numeroLimpo = this.normalizarNumero(this.numero);
    if (!numeroLimpo) {
      this.alertComponent.showAlert('Informe um número para bloquear.', 'error');
      return;
    }
    if (!/^\d{10,11}$/.test(numeroLimpo)) {
      this.alertComponent.showAlert('Formato inválido. Use (99) 99999-9999.', 'error');
      return;
    }
    if (this.listBloqueados.some((item) => this.normalizarNumero(item.number) === numeroLimpo)) {
      this.alertComponent.showAlert('Número já bloqueado.', 'error');
      return;
    }

    this.loadingComponent.activeLoading();
    try {
      const response = await this.bloqueadoService
        .setBloquear(numeroLimpo)
        .toPromise();
      const newBlockedNumber = { number: this.formatPhoneNumber(numeroLimpo) };

      this.listBloqueados.unshift(newBlockedNumber);
      this.ordenarLista();
      this.recalcularPaginacao();

      this.loadingComponent.inactiveLoading();
      this.alertComponent.showAlert('Número bloqueado com sucesso!', 'success');
      this.closeModalRegistroBloqueado();
    } catch (error: any) {
      console.log(error?.error);
      this.alertComponent.showAlert('Erro ao tentar bloquear número', 'error');
      this.closeModalRegistroBloqueado();
    }
  }

  public onFilterChange() {
    this.pageInfo.currentPage = 1;
    this.recalcularPaginacao();
  }

  public permitirSomenteNumeros(event: KeyboardEvent) {
    const key = event.key;
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'];
    if (!/[0-9]/.test(key) && !allowed.includes(key)) {
      event.preventDefault();
    }
  }

  public onNumeroInput() {
    const apenasDigitos = this.normalizarNumero(this.numero).slice(0, 11);
    this.numero = this.formatPhoneNumber(apenasDigitos);
  }

  private normalizarNumero(numero: string): string {
    return (numero || '').replace(/\D/g, '');
  }
}
